<div class="site-container">

  <!-- Loading Spinner -->
  @if (loading) {
    <div class="loading-container">
      <mat-spinner diameter="60"></mat-spinner>
      <p>Loading site...</p>
    </div>
  }

  @if (!loading) {
    <!-- Header Section -->
    <div class="header-section">
      <div class="title-section">
        <h1>Site Details</h1>
        @if (isInactive) {
          <mat-chip class="inactive-chip" color="warn">
            <mat-icon>cancel</mat-icon>
            INACTIVE
          </mat-chip>
        }
      </div>

      <div class="header-actions">
        <button mat-stroked-button (click)="goBack()">
          <mat-icon>arrow_back</mat-icon>
          Back to List
        </button>
        <button mat-raised-button color="primary"
                [disabled]="!canEdit"
                (click)="editSite()">
          <mat-icon>edit</mat-icon>
          Edit
        </button>
      </div>
    </div>

    <h2 class="entity-name">{{ currentSite.name }}</h2>

    <!-- Main Information Card -->
    <mat-card class="info-card">
      <mat-card-header>
        <mat-card-title>
          <mat-icon>location_on</mat-icon>
          Site Information
        </mat-card-title>
      </mat-card-header>
      <mat-card-content>
        <div class="info-grid">

          <!-- Site Name -->
          <mat-form-field appearance="outline" class="full-width">
            <mat-label>Site Name</mat-label>
            <input matInput
                   [value]="currentSite.name"
                   readonly>
            <mat-icon matSuffix>business</mat-icon>
          </mat-form-field>

          <!-- Formerly Known As -->
          @if (currentSite.fka) {
            <mat-form-field appearance="outline" class="full-width">
              <mat-label>Formerly Known As</mat-label>
              <input matInput
                     [value]="currentSite.fka"
                     readonly>
              <mat-icon matSuffix>history</mat-icon>
            </mat-form-field>
          }

          <!-- Site Number -->
          <mat-form-field appearance="outline">
            <mat-label>Site Number</mat-label>
            <input matInput
                   [value]="currentSite.siteNum"
                   readonly>
            <mat-icon matSuffix>tag</mat-icon>
          </mat-form-field>

          <!-- DOP Number -->
          @if (currentSite.dopNum) {
            <mat-form-field appearance="outline">
              <mat-label>DOP Number</mat-label>
              <input matInput
                     [value]="currentSite.dopNum"
                     readonly>
              <mat-icon matSuffix>numbers</mat-icon>
            </mat-form-field>
          }

          <!-- Division -->
          @if (currentSite.division) {
            <mat-form-field appearance="outline">
              <mat-label>Division</mat-label>
              <input matInput
                     [value]="currentSite.division"
                     readonly>
              <mat-icon matSuffix>account_tree</mat-icon>
            </mat-form-field>
          }

          <!-- District -->
          @if (currentSite.district) {
            <mat-form-field appearance="outline">
              <mat-label>District</mat-label>
              <input matInput
                     [value]="currentSite.district"
                     readonly>
              <mat-icon matSuffix>domain</mat-icon>
            </mat-form-field>
          }

          <!-- Square Footage -->
          @if (currentSite.squareFootage) {
            <mat-form-field appearance="outline">
              <mat-label>Square Footage</mat-label>
              <input matInput
                     [value]="currentSite.squareFootage"
                     readonly>
              <mat-icon matSuffix>square_foot</mat-icon>
            </mat-form-field>
          }

          <!-- Program -->
          @if (currentSite.program) {
            <mat-form-field appearance="outline">
              <mat-label>Program</mat-label>
              <input matInput
                     [value]="currentSite.program"
                     readonly>
              <mat-icon matSuffix>category</mat-icon>
            </mat-form-field>
          }

        </div>
      </mat-card-content>
    </mat-card>

    <!-- Address Information Card -->
    <mat-card class="info-card">
      <mat-card-header>
        <mat-card-title>
          <mat-icon>place</mat-icon>
          Address Information
        </mat-card-title>
      </mat-card-header>
      <mat-card-content>
        <div class="info-grid">

          <!-- Address -->
          <mat-form-field appearance="outline" class="full-width">
            <mat-label>Address</mat-label>
            <textarea matInput
                      [value]="currentSite.address"
                      rows="2"
                      readonly>
            </textarea>
            <mat-icon matSuffix>home</mat-icon>
          </mat-form-field>

          <!-- City -->
          <mat-form-field appearance="outline">
            <mat-label>City</mat-label>
            <input matInput
                   [value]="currentSite.city"
                   readonly>
            <mat-icon matSuffix>location_city</mat-icon>
          </mat-form-field>

          <!-- State -->
          <mat-form-field appearance="outline">
            <mat-label>State</mat-label>
            <input matInput
                   [value]="currentSite.state"
                   readonly>
            <mat-icon matSuffix>map</mat-icon>
          </mat-form-field>

          <!-- ZIP -->
          <mat-form-field appearance="outline">
            <mat-label>ZIP Code</mat-label>
            <input matInput
                   [value]="currentSite.zip"
                   readonly>
            <mat-icon matSuffix>markunread_mailbox</mat-icon>
          </mat-form-field>

          <!-- County -->
          @if (currentSite.county) {
            <mat-form-field appearance="outline">
              <mat-label>County</mat-label>
              <input matInput
                     [value]="currentSite.county"
                     readonly>
              <mat-icon matSuffix>terrain</mat-icon>
            </mat-form-field>
          }

        </div>
      </mat-card-content>
    </mat-card>

    <!-- Status Card -->
    <mat-card class="info-card">
      <mat-card-header>
        <mat-card-title>
          <mat-icon>info</mat-icon>
          Status
        </mat-card-title>
      </mat-card-header>
      <mat-card-content>
        <div class="status-container">
          <mat-slide-toggle
            [checked]="isInactive"
            [disabled]="true">
            <span [class.active]="!isInactive" [class.inactive]="isInactive">
              @if (!isInactive) {
                <mat-icon>check_circle</mat-icon>
                Active
              } @else {
                <mat-icon>cancel</mat-icon>
                Inactive
              }
            </span>
          </mat-slide-toggle>
        </div>
      </mat-card-content>
    </mat-card>

    <!-- Comments Card -->
    @if (currentSite.comments) {
      <mat-card class="info-card">
        <mat-card-header>
          <mat-card-title>
            <mat-icon>note</mat-icon>
            Comments & Notes
          </mat-card-title>
        </mat-card-header>
        <mat-card-content>
          <div class="comments-display" [innerHTML]="currentSite.comments"></div>
        </mat-card-content>
      </mat-card>
    }

    <!-- Associated Contracts Card -->
    <mat-card class="info-card">
      <mat-card-header>
        <mat-card-title>
          <mat-icon>description</mat-icon>
          Associated Contracts
        </mat-card-title>
      </mat-card-header>
      <mat-card-content>
        @if (hasContracts) {
          <ag-grid-angular
            style="width: 100%; height: 400px;"
            class="ag-theme-balham"
            [rowData]="contractsAssociatedWithThisSite"
            [columnDefs]="contractCols"
            [defaultColDef]="defaultColDef"
            (gridReady)="onGridReady($event)"
            (cellClicked)="onCellClicked($event)">
          </ag-grid-angular>
        } @else {
          <div class="no-contracts">
            <mat-icon>folder_open</mat-icon>
            <p>No contracts associated with this site</p>
          </div>
        }
      </mat-card-content>
    </mat-card>

  }

</div>
